<template>
  <div class="gallery-element-container" ref="container" :class="{initialized}" v-click-outside="onClickOutside">
    <template v-for="(photo, i) in photos">
      <v-img
        class="d-block gallery-element no-hover-fade"
        v-if="photo.details.photo.image.mimeType.indexOf('image/') !== -1"
        :key="photo.details.photo.image.id"
        :src="optimizedImageURL(photo.details.photo.image, {breakpointSize: $vuetify.breakpoint, quality: 90})"
        :lazy-src="getLazySrc(photo.details.photo.image)"
        @click="onPhotoClick(photo)"
        @mouseenter="$emit('mouseenter', photo)"
        @mouseleave="$emit('mouseleave', photo)"
        :data-cursor-target="getCursor(photo, i)"
        :style="photoStyle(photo, i)"
        contain
      />
      <v-lazy
        class="gallery-element fill-height no-hover-fade"
        v-if="photo.details.photo.image.mimeType.indexOf('video/') !== -1"
        :key="photo.details.photo.image.id"
        :data-cursor-target="getCursor(photo, i)"
        :style="photoStyle(photo, i)"
        @click="onPhotoClick(photo)"
        @mouseenter="$emit('mouseenter', photo)"
        @mouseleave="$emit('mouseleave', photo)"
        >
        <!-- min-height="100" -->
        <VuePlyr :style="{position: 'relative', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: '#000'}" :options="{controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']}">
          <video :style="{width: '100%', height: '100%'}" >
            <source :src="`/media/${photo.details.photo.image.id}`" :type="photo.details.photo.image.mimeType">
          </video>
        </VuePlyr>
      </v-lazy>

    </template>
  </div>
</template>

<script>
import delay from 'lodash/delay'
import reject from 'lodash/reject'
import { Viewport } from '@monogrid/vue-lib'
import { optimizedImageURL, getLazySrc, getAspectRatio, getWidth, getHeight } from '@/services/OptimizedImage'
import VuePlyr from 'vue-plyr'
import 'plyr/dist/plyr.css'

export default {
  name: 'PhotoExhibitionElement',
  mixins: [Viewport],

  components: {
    VuePlyr
  },

  props: {
    data: {
      type: Object,
      required: true
    },
    position: {
      type: String,
      required: false
    }
  },

  data () {
    return {
      refAvailable: false,
      initialized: false,
      selectedPhoto: null
    }
  },

  computed: {
    photos () {
      // console.log(this.data.photos)
      return reject(this.data.photos, { details: { photo: { image: null } } })
    }
  },

  mounted () {
    this.tryToFindRef()
  },

  watch: {
    selectedPhoto (value) {
      this.$emit('click', value)
    },
    async viewPort () {
      this.refAvailable = false
      await this.$nextTick()
      this.refAvailable = true
    }
  },

  methods: {
    optimizedImageURL,
    getLazySrc,
    getAspectRatio,
    onClickOutside () {
      this.selectedPhoto = null
      // this.$emit('click', null)
    },
    async tryToFindRef () {
      if (this.$refs.container) {
        this.refAvailable = true
        delay(() => { this.initialized = true }, 10)
      } else {
        delay(this.tryToFindRef, 10)
      }
    },
    getCursor (photo, i) {
      return this.data.photos.length > 1 && this.position === 'center' ? this.selectedPhoto ? 'zoom-in||1|45' : 'zoom-in||1|0' : null
    },
    photoStyle (photo, i) {
      if (!this.refAvailable) return {}

      const spacing = 8

      let metaW
      let metaH
      if (photo.details.photo.image.mimeType.indexOf('image/') !== -1) {
        metaW = getWidth(photo.details.photo.image)
        metaH = getHeight(photo.details.photo.image)
      } else if (photo.details.photo.image.mimeType.indexOf('video/') !== -1) {
        metaW = 1920
        metaH = 1080
      }

      const totalRows = Math.ceil(this.data.photos.length / 2)
      const totalCols = this.data.photos.length > 2 ? 2 : 1

      const currentRow = Math.floor(i / totalRows)
      const currentCol = Math.floor(i % totalRows)

      const bounds = this.$refs.container.getBoundingClientRect()

      const minW = (bounds.width - spacing) / totalRows

      const h = Math.round(Math.min((minW * metaH) / metaW, (bounds.height / totalRows) - spacing))
      const w = Math.round(Math.min(minW, (h * metaW) / metaH))

      const offsetx = (((w + spacing) / totalCols) * (totalCols - 1))

      let x = 0// align left
      if (this.position === 'center') {
        x = ((bounds.width - w) / 2) - offsetx// center
      } else if (this.position === 'right') {
        x = bounds.width - w - (offsetx * 2)// right
      }

      x += ((w + spacing) * (currentCol))

      let y = (bounds.height - (h + spacing / 2)) / 2

      y += ((h + spacing) * (currentRow)) - ((h / totalRows) * (totalRows - 1))

      return {
        opacity: this.selectedPhoto && this.selectedPhoto !== photo ? '0' : null,
        zIndex: photo === this.selectedPhoto ? 1 : 0,
        transform: photo === this.selectedPhoto ? 'translate3d(0, 0, 0)' : `translate3d(${x}px, ${y}px, 0)`,
        // cursor: this.data.photos.length > 1 && this.position === 'center' ? this.selectedPhoto ? 'zoom-out' : 'zoom-in' : null,
        height: photo === this.selectedPhoto ? '100%' : `${h}px`,
        width: photo === this.selectedPhoto ? '100%' : `${w}px`,
        cursor: this.data.photos.length > 1 && this.position === 'center' ? this.selectedPhoto ? 'pointer' : 'pointer' : null
      }
    },
    onPhotoClick (photo) {
      if (this.data.photos.length === 1) return

      if (this.selectedPhoto === photo) {
        this.selectedPhoto = null
      } else {
        this.selectedPhoto = photo
      }
      // this.$emit('click', this.selectedPhoto)
    }
  }
}
</script>

<style lang="scss" scoped>

.gallery-element-container {
  position: relative;
}

.gallery-element {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: '0% 0%';
  will-change: transform, opacity, width, height;
  backface-visibility: hidden;

  .initialized & {
    transition: width 0.35s $ease-in-out-quad, height 0.35s $ease-in-out-quad, opacity 0.35s $ease-in-out-quad, transform 0.35s $ease-in-out-quad;
  }
}

</style>
